import React, { useEffect, useRef, useState } from 'react';
import type { RuleSet } from 'styled-components';
import styled, { css } from 'styled-components';
import { IconArrowLeftLinear, IconArrowRightLinear, IconDetails } from '@/app/common/icons/libraryIcons';
import { useKey } from 'react-use';
import { useTranslation } from '@/app/components/translations/Translation';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { focusRing } from '@/app/components/search/styles/mixins';
import { Text } from '@lmn/design-system';
import { Icon } from '@/app/common/icons/Icon';
import { keyDownHandler } from '@/app/components/hotel-detail/common/common.helper';
import { firstPhotoSlide, toPhotoIndex } from '@/app/components/hotel-detail/hooks/useHeroStrip';

type HeroGallerySliderOverlayProps = {
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null> | null;
  amountOfSlides: number;
  // Max number of slides to show in the counter. To fit cases where we show only x images but we want to indicate that there are more
  // For example, it shows 1/20, but in the slider we only shpow 5 and then you can click to open a gallery to see all
  counterTotalSlides?: number;
  slideChanged?: (imageNumber: number) => void;
  openGalleryClick?: (event?: React.SyntheticEvent<Element, Event>) => void;
  counterDisplay?: 'none' | 'default' | 'hero';
  initialImageIndex?: number;
  /**
   * For a strip whose slides are replaced under the reader — the hero's, when the hotel's own photos
   * arrive — keep where it already sits instead of placing it on `initialImageIndex` again. Off by
   * default: a caller that drives `initialImageIndex` (the full-screen gallery's arrows) has to be
   * able to ask for the first slide while the strip is scrolled (MPH-1002).
   */
  keepScrollPosition?: boolean;
  resetScrollType?: 'instant' | 'smooth';
  scrollType?: 'auto' | 'smooth';
  useKeyNavigation?: boolean;
  showCounterIcon?: boolean;
  hidePagination?: boolean;
  /** Makes the default counter a button too, opening the gallery on the photo it shows (MPH-1213). */
  onCounterClick?: (slideIndex: number) => void;
  counterPosition?: RuleSet<object> | string;
  /** A copy of the last photo opens the strip, ahead of the first, and counts as that photo (MPH-1237). */
  hasLeadingCopy?: boolean;
};

const PaginationButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  cursor: pointer;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  width: 2rem;
  height: 2rem;
  color: ${({ theme }) => theme.kallisto.color.icon.static.iconOnDark};
  border: none;
  background: transparent;

  & > [data-library-icon] {
    width: 2rem;
    height: 2rem;

    @media ${customCss.breakpoints.sm} {
      width: 1.5rem;
      height: 1.5rem;

      &:focus,
      &:hover {
        filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5));
      }
    }
  }
`;

/**
 * adds Controls and functionality to the image gallery that is only needed for mobile views where there is direct swiping and paging support.
 *
 * Contains two **paging** controls for going to the **next** and **previous** image.
 * Contains logic to calculate the number of the currently selected slide.
 *
 * At the beginning, when showing the first image, if the user clicks the left arrow, the slider scrolls to the right
 * to the last position. This effect can be a little bit weird and also causes issues preloading images because
 * all the images will be visible in the viewport during the scroll.
 * This happens also with the right arrow if we are in the last image.
 * To avoid this when there are lot of images, it uses a direct change in scroll position, without smooth animation.
 *
 * TODOs:
 * - style the elements as defined in the invision designs
 * - optimize scroll handling. Currently the selected page is calculated upon every scroll event. This can be debounced.
 * - optimize scroll handling #2. Slide calculation calls getBoundingClientRect every time.
 * This is expensive & could be improved via `Resize Observer` or similar
 *
 */
export default function HeroGallerySliderOverlay(props: HeroGallerySliderOverlayProps) {
  const {
    scrollContainerRef,
    amountOfSlides,
    slideChanged,
    openGalleryClick,
    counterDisplay = 'default',
    counterTotalSlides = amountOfSlides,
    scrollType = 'smooth',
    resetScrollType = 'smooth',
    initialImageIndex = 0,
    keepScrollPosition = false,
    useKeyNavigation = false,
    showCounterIcon = true,
    hidePagination = false,
    onCounterClick,
    counterPosition,
    hasLeadingCopy = false,
  } = props;

  const [currentSlide, setCurrentSlide] = useState(0);
  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const keyScrollBehavior = useRef(scrollType);
  // Read by the scroll listener: the copy goes in with the strip moved under it in the same frame,
  // and the scroll that causes must already be counted with it. The arrows read it too, as their key
  // bindings keep the handlers of the first render.
  const hasLeadingCopyRef = useRef(hasLeadingCopy);
  hasLeadingCopyRef.current = hasLeadingCopy;

  const onPrevClick = () => {
    if (!scrollContainerRef?.current) return;
    const containerScrollLeft = scrollContainerRef.current.scrollLeft;
    const slideWidth = scrollContainerRef.current.getBoundingClientRect().width;
    const wraps = containerScrollLeft <= 0;

    // Wraps to the last photo, not to the end of the strip: the mobile hero's ends with a copy of the first,
    // and may open with a copy of the last ahead of the real one.
    const scrollTo = wraps
      ? slideWidth * (amountOfSlides - 1 + firstPhotoSlide(hasLeadingCopyRef.current))
      : containerScrollLeft - slideWidth;

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    wraps && resetScrollType === 'instant'
      ? scrollContainerRef.current.scroll(scrollTo, 0)
      : scrollContainerRef.current.scroll({ left: scrollTo, behavior: keyScrollBehavior.current });
  };

  const onNextClick = () => {
    if (!scrollContainerRef?.current) return;

    const containerWidth = scrollContainerRef.current.getBoundingClientRect().width;
    const containerScrollWidth = scrollContainerRef.current.scrollWidth;
    const containerScrollLeft = scrollContainerRef.current.scrollLeft;

    // +40 is some kind of offset value to avoid some cases where scroll left + container width is not exactly the total scroll width (e.g. full screen mobile landscape)
    const wraps = containerScrollLeft + containerWidth + 40 >= containerScrollWidth;
    // Wraps to the first photo, which comes after the copy of the last when one opens the strip.
    const scrollTo = wraps
      ? containerWidth * firstPhotoSlide(hasLeadingCopyRef.current)
      : containerScrollLeft + containerWidth;

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    wraps && resetScrollType === 'instant'
      ? scrollContainerRef.current.scroll(scrollTo, 0)
      : scrollContainerRef.current.scroll({ left: scrollTo, behavior: keyScrollBehavior.current });
  };

  useKey('ArrowRight', () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    useKeyNavigation && onNextClick();
  });
  useKey('ArrowLeft', () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    useKeyNavigation && onPrevClick();
  });

  useEffect(() => {
    function onScroll() {
      const scrollContainer = scrollContainerRef ? scrollContainerRef.current : null;

      // The slides are as wide as the strip, which may hold more than `amountOfSlides`: the mobile
      // hero's opens with a copy of its last photo and ends with a copy of its first.
      const imageSlideWidth = scrollContainer?.getBoundingClientRect().width;
      if (!scrollContainer || !imageSlideWidth) return;
      const currentIndex = Math.floor((scrollContainer.scrollLeft + imageSlideWidth / 2) / imageSlideWidth);
      setCurrentSlide(toPhotoIndex(currentIndex, amountOfSlides, hasLeadingCopyRef.current));
    }
    const scrollContainer = scrollContainerRef ? scrollContainerRef.current : null;
    scrollContainer?.addEventListener('scroll', onScroll, true);
    return () => scrollContainer?.removeEventListener('scroll', onScroll, true);
  }, [scrollContainerRef, amountOfSlides]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    slideChanged && slideChanged(currentSlide);
    keyScrollBehavior.current = scrollType;
  }, [slideChanged, currentSlide]);

  useEffect(() => {
    const container = scrollContainerRef?.current;

    if (!container) {
      setCurrentSlide(initialImageIndex);
      return;
    }

    const slideWidth = container.getBoundingClientRect().width;

    /*
     * Whenever this effect does run again on a strip the reader has already scrolled, hold where it
     * sits instead of placing it on the index we were handed. Only a caller whose index is always
     * the first slide opts in: the full-screen gallery's arrows pass 0 to mean "go to the first
     * photo", and must still be obeyed.
     *
     * What re-runs it for the hero is not settled. The deps are all stable there and the overlay
     * keeps its slot across the photo swap, so by reading it should never re-run — yet dropping
     * this branch brought the reported jump back. It costs nothing to keep, the hero's index being
     * always 0, so it stays until someone catches the remount in the act (MPH-1002).
     */
    // A copy of the last photo ahead of the first makes the start of the strip that copy rather than
    // the first photo, so a strip resting there has been scrolled too (MPH-1237).
    if (keepScrollPosition && initialImageIndex === 0 && (container.scrollLeft > 0 || hasLeadingCopy)) {
      // The mobile hero's strip can rest on a copy at either end, which counts as the photo it repeats.
      if (slideWidth > 0) {
        setCurrentSlide(toPhotoIndex(Math.round(container.scrollLeft / slideWidth), amountOfSlides, hasLeadingCopy));
      }
      return;
    }

    setCurrentSlide(initialImageIndex);
    container.scroll(slideWidth * (initialImageIndex + firstPhotoSlide(hasLeadingCopy)), 0);
    // Not on `amountOfSlides`: the count grows when the hotel's own photos arrive, and re-placing the
    // strip then is the very jump this effect guards against.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialImageIndex, keepScrollPosition, scrollContainerRef]);

  useEffect(() => {
    const onResize = () => {
      if (!scrollContainerRef?.current) return;
      const containerWidth = scrollContainerRef.current.getBoundingClientRect().width;
      const scrollTo = containerWidth * (currentSlide + firstPhotoSlide(hasLeadingCopy));
      scrollContainerRef.current.scroll(scrollTo, 0);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [currentSlide, scrollContainerRef, hasLeadingCopy]);

  return (
    <>
      {hidePagination || amountOfSlides <= 1 ? null : (
        <>
          <PaginationButton
            css={css`
              left: 0.5rem;

              @media ${customCss.breakpoints.sm} {
                left: 0rem;
              }
            `}
            onClick={() => {
              sendCuriosityClickEvent({
                event_name: 'click_previous_picture',
                event_context: 'gallery',
              });
              onPrevClick();
            }}
            className="slider-overlay-arrow-left"
          >
            <IconArrowLeftLinear title={t('vacation.common.pagination.prevpage')} />
          </PaginationButton>
          <PaginationButton
            css={css`
              right: 0.5rem;

              @media ${customCss.breakpoints.sm} {
                right: 0rem;
              }
            `}
            onClick={() => {
              sendCuriosityClickEvent({
                event_name: 'click_next_picture',
                event_context: 'gallery',
              });
              onNextClick();
            }}
            className="slider-overlay-arrow-right"
          >
            <IconArrowRightLinear style={{ right: '0.5rem' }} title={t('vacation.common.pagination.nextpage')} />
          </PaginationButton>
        </>
      )}

      {counterDisplay === 'default' && (
        <DefaultImageCounter
          showIcon={showCounterIcon}
          currentSlide={currentSlide}
          amountOfSlides={counterTotalSlides}
          counterPosition={counterPosition}
          onClick={onCounterClick ? () => onCounterClick(currentSlide) : undefined}
          label={t('vacation.select.details.gallery.summary.watchall')}
        />
      )}
      {counterDisplay === 'hero' && (
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            background: linear-gradient(-180deg, rgba(19, 19, 19, 0) 0%, rgba(8, 8, 8, 0.6) 100%);
            position: absolute;
            bottom: 0;
            color: ${({ theme }) => theme.kallisto.color.text.static.textOnDark};
            padding: 0.5rem 1rem;
          `}
        >
          <div
            onClick={openGalleryClick}
            onKeyPress={openGalleryClick}
            role="button"
            tabIndex={0}
            css={`
              display: flex;
              align-items: center;
              padding: 0 0.5rem 0 0;
              border-radius: 0.25rem;
            `}
          >
            <IconDetails
              css={`
                width: 1rem;
                height: 1rem;
              `}
            />
            <span
              css={css`
                padding: 0.25rem 0.25rem 0.25rem 0.5rem;
                font-size: ${(p) => p.theme.kallisto.font.size['175']};
              `}
            >
              {t('vacation.select.details.gallery.summary.watchall')}
            </span>
          </div>
          <span
            css={css`
              font-size: ${(p) => p.theme.kallisto.font.size['175']};
            `}
          >
            {currentSlide + 1}/{counterTotalSlides}
          </span>
        </div>
      )}
    </>
  );
}

const DefaultImageCounterPosition = css`
  bottom: 8px;
  right: 8px;

  @media ${customCss.breakpoints.sm} {
    bottom: 16px;
    right: 16px;
  }
`;

export function DefaultImageCounter(props: {
  showIcon: boolean;
  currentSlide: number;
  amountOfSlides: number;
  counterPosition?: RuleSet<object> | string;
  onClick?: () => void;
  label?: string;
}) {
  const {
    showIcon,
    currentSlide,
    amountOfSlides,
    counterPosition = DefaultImageCounterPosition,
    onClick,
    label,
  } = props;
  return (
    <div
      {...(onClick && {
        role: 'button',
        tabIndex: 0,
        'aria-label': label ? `${label} (${currentSlide + 1} / ${amountOfSlides})` : undefined,
        onClick,
        onKeyDown: keyDownHandler(onClick),
      })}
      css={css`
        display: flex;
        align-items: center;
        background: rgb(61 61 61 / 80%);
        position: absolute;
        color: ${({ theme }) => theme.kallisto.color.text.static.textOnDark};
        padding: 0 8px;
        column-gap: 8px;
        border-radius: 4px;
        height: 28px;
        cursor: ${onClick ? 'pointer' : 'default'};

        &:focus-visible {
          ${focusRing}
        }

        ${counterPosition}
      `}
    >
      {showIcon && <Icon category="UI Essentials" name="icon-photo-gallery" size={14} color="iconOnDark" />}
      <Text variant="Caption" color="textOnDark">
        {`${currentSlide + 1} / ${amountOfSlides}`}
      </Text>
    </div>
  );
}
